import type {
  ComponentType,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
} from "react"

import { usePermission, type PermissionRequirement } from "~/hooks/usePermission"

import { DisabledTooltip, type TooltipPlacement } from "./DisabledTooltip"

/**
 * The gating props every permission-aware primitive accepts. Spread into the primitive's prop
 * type and pair with {@link usePermissionGate}.
 */
export type PermissionGateProps = {
  /** Permission tier this action requires, mirroring the tRPC procedure ladder. The control
   * disables itself below the tier and renders its own permission tooltip. Cannot gate a slot:
   * not with `asChild` or `href`, and not while the control is itself slotted — put `requires`
   * on the enclosing gated `*Trigger` (Dialog/Drawer/Popover/DropdownMenu), or wrap in
   * `PermissionTooltip` when there is none. See AUTH0.md § Gating UI. */
  requires?: PermissionRequirement
  /** Overrides the default denial copy while blocked by `requires`. */
  permissionReason?: string
  /** Contextual copy shown while the user IS allowed — an action hint or a not-applicable
   * explanation; the denial copy wins while blocked. Never nest a second `Tooltip` for this:
   * disabled controls still receive pointer events in some browsers, so both would open. */
  allowedTitle?: string
  /** Side of the `requires` tooltip. */
  tooltipSide?: TooltipPlacement["side"]
  /** Extends the wrapper `span` the gate adds. Pass `"w-full"` when the parent stretches the
   * control, which the span would otherwise shrink to its content width. Primitives that are
   * full-width by construction default to it. */
  wrapperClassName?: string
}

/**
 * What {@link usePermissionGate} reads: the gate props plus the bubble's own class, for a surface
 * whose tooltips carry chrome of their own — the gate's bubble is the control's only one, so it
 * has to be able to look like theirs. Not in `PermissionGateProps`, because a primitive that does
 * not declare the prop would spread it onto the DOM; a primitive opts in by extending this.
 */
export type PermissionGateOptions = PermissionGateProps & { tooltipClassName?: string }

const passThrough = (control: ReactNode) => control

/**
 * A gated control's disabled state and its single tooltip — one implementation shared by every
 * primitive. `gate` returns an ungated control untouched, paying no extra element, and otherwise
 * always wraps it, whether or not there is anything to explain right now: the wrapper depends
 * only on `requires`, so a change of tier or `allowedTitle` never remounts the control.
 *
 * `defaultWrapperClassName` is the primitive's own layout requirement, overridden by a caller's
 * `wrapperClassName` — only the caller knows the surrounding layout.
 */
export function usePermissionGate(
  {
    requires,
    permissionReason,
    allowedTitle,
    tooltipSide,
    tooltipClassName,
    wrapperClassName,
  }: PermissionGateOptions,
  defaultWrapperClassName?: string,
): { blocked: boolean; gate: (control: ReactNode) => ReactNode } {
  const { allowed, reason } = usePermission(requires)
  if (requires === undefined) return { blocked: false, gate: passThrough }

  const title = allowed ? allowedTitle : (permissionReason ?? reason)
  return {
    blocked: !allowed,
    gate: (control) => (
      <DisabledTooltip
        title={title}
        wrapperClassName={wrapperClassName ?? defaultWrapperClassName}
        side={tooltipSide}
        className={tooltipClassName}
      >
        {control}
      </DisabledTooltip>
    ),
  }
}

type TriggerLikeProps = {
  disabled?: boolean
  onClick?: MouseEventHandler<never>
}

const swallowClick = (e: MouseEvent) => e.preventDefault()

/**
 * Teaches a Radix `*Trigger` wrapper the `PermissionGateProps` contract, so a slotted control
 * is gated from outside its slot: `<DialogTrigger requires="edit" asChild><Button>…`. Ungated
 * (`requires` undefined) the trigger renders exactly as the bare primitive — no wrapper span,
 * `disabled` passed through untouched. While blocked it force-disables the trigger (with
 * `asChild`, the slot child) and swallows the click via `preventDefault`, which Radix honors
 * before opening — so the surface stays closed even if a slot child's own `disabled` prop
 * shadows the injected one. The trigger owns `disabled`: Radix slot merging lets a child's
 * `disabled` win over the gate's, so children of a gated trigger must not carry their own.
 */
export function withPermissionGate<P extends TriggerLikeProps>(
  Trigger: ComponentType<P>,
  displayName: string,
) {
  function GatedTrigger({
    requires,
    permissionReason,
    allowedTitle,
    tooltipSide,
    wrapperClassName,
    disabled,
    onClick,
    ...props
  }: P & PermissionGateProps) {
    const { blocked, gate } = usePermissionGate({
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
    })
    return gate(
      <Trigger
        {...(props as P)}
        disabled={blocked ? true : disabled}
        onClick={blocked ? swallowClick : onClick}
      />,
    )
  }
  GatedTrigger.displayName = displayName
  return GatedTrigger
}

type Props = TooltipPlacement & {
  requires: PermissionRequirement
  /** Overrides the default blocked copy, for controls that name the specific action. */
  reason?: string
  /** Contextual copy while the user IS allowed, so the control never needs a second `Tooltip`.
   * See {@link PermissionGateProps.allowedTitle}. */
  allowedTitle?: string
  /** Extends the wrapper `span`, for children a parent stretches. */
  wrapperClassName?: string
}

/**
 * The single tooltip on a permission-gated control: the permission reason when blocked,
 * `allowedTitle` when allowed, no bubble when there is neither. The same machinery as the
 * primitives' `requires` prop, for controls that have no such prop — the escape hatch for
 * controls no `requires`-capable primitive or gated `*Trigger` covers. Note it only explains;
 * the control still needs its own `disabled` wiring.
 * Canonical intent: {@link ../design-system/intents.ts PERMISSION_TOOLTIP_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export function PermissionTooltip({
  requires,
  reason,
  allowedTitle,
  children,
  ...rest
}: PropsWithChildren<Props>) {
  const { allowed, reason: defaultReason } = usePermission(requires)

  return (
    <DisabledTooltip title={allowed ? allowedTitle : (reason ?? defaultReason)} {...rest}>
      {children}
    </DisabledTooltip>
  )
}
